import camelCase from 'lodash/camelCase';
import kebabCase from 'lodash/kebabCase';
import uniqueId from 'lodash/uniqueId';
import jwt from 'jsonwebtoken';
import {
  CREATE,
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  UPDATE,
  DELETE,
  DELETE_MANY,
  UPDATE_MANY,
} from 'react-admin';
import queryString from 'qs';
import _ from 'lodash';

import { getAuthToken } from './authProvider';

import {
  API_TOP_LEVEL_RESOURCE_ALL_URL,
  API_TOP_LEVEL_RESOURCE_ONE_URL,
  API_TOP_LEVEL_RESOURCE_APPROVE_URL,
  API_ACCOUNT_OFFER_ORDERS_URL,
  API_QUOTES_PRIVATE_QUOTES_ALL_URL,
  API_QUOTES_PRIVATE_QUOTES_URL_EDITFORMAT,
  API_QUOTES_PRIVATE_QUOTES_URL,
  API_FOLIO_URLS,
  replacePlaceholders,
  API_USER_URL,
  API_OFFERINGS_URL,
  API_UPLOAD_TOKEN_URL,
  API_SEND_RESET_PASSWORD_URL,
  API_ADMIN_STATISICS_URL,
  API_ADMIN_CHART_DATA_URL,
  API_NOTIFICATIONS_URL,
  API_NOTIFICATION_REGISTRATIONS_URL,
  API_AUTH_CREDENTIALS_URL,
  API_USER_FILES_ONE_URL,
  API_USER_CHANGE_STATUS_URL,
  API_OFFER_ORDER_URL,
  API_UPLOAD_OFFER_ORDERS_FOLIO_URL,
  API_OFFERING_ORDER_URL,
  API_OFFERING_DOCUMENTS_URL,
  API_USER_TRUSTED_CONTACT_URL,
  API_OFFERING_ACTIONS_URL,
  API_OFFER_CAMPAIGNS_URL,
  API_OFFER_CAMPAIGN_URL,
  API_OFFER_CAMPAIGN_SEND_URL,
  API_OFFER_CAMPAIGN_TEST_SEND_URL,
  API_QUOTES_PRIVATE_QUOTES_FOLIO_URL,
  API_EMAIL_TEMPLATES,
  API_EMAIL_TEMPLATE,
  API_MAILCHIMP_URLS,
  API_OFFER_CAMPAIGN_EVALUATE_URL,
  API_EXTERNAL_OFFER_ORDERS_URL,
  API_USERS_URL,
  EXTERNAL_OFFER_ORDER_STATUS_EMAIL,
  API_CONFIGS_URL,
  API_ACCOUNT_CLOSE_ACCOUNT_URL,
} from './urls';
import callWebApi from './callWebApi';

// NOTE: react-admin provider layer (communicates with the server)

const get = (url, params, replacements, { includeAuth = true } = {}) => {
  let renderedUrl = replacePlaceholders(url, replacements);
  const qs = queryString.stringify(params);
  if (qs) {
    renderedUrl = `${renderedUrl}?${qs}`;
  }
  const authToken = includeAuth ? getAuthToken() : undefined;
  return callWebApi('GET', renderedUrl, undefined, authToken);
};


const debug = require('debug')('cambria:dataProvider');


export const ACCOUNT_RESOURCES = [
  'accountPerformanceSummary',
  'accountTransactions',
  'accountRestrictions',
  'accountCashBalance',
  'accountPositions',
  'userActions',
];


const TOP_LEVEL_RESOURCES = [
  'users',
  'accounts',
  'offerings',
  'offeringDocuments',
  'offeringOrders',
  'offeringWatches',
  'offeringCampaigns',
  'userActions',
  'emailTemplates',
  'externalOfferOrders',
  'solicitedUsers',
  'configs',
];


async function uploadFile(key, rawFile) {
  const token = await callWebApi('POST', API_UPLOAD_TOKEN_URL, { key }, getAuthToken());

  const formData = new FormData();
  Object.entries(token.data.fields).forEach(([k, v]) => {
    formData.append(k, v);
  });
  formData.append('Content-Type', rawFile.type);
  formData.append('file', rawFile);
  const res = await fetch(token.data.url, { method: 'POST', body: formData });
  if (!res.ok) {
    throw new Error(`Upload failed. key=${token.data.fields.key}`);
  }
  // The upload url looks like https://s3.us-west-2.amazonaws.com/assets.qa.myipo.com
  // so we can use the last part to figure out the public URL to the uploaded asset.
  // This assumes https://assets.qa.myipo.com is set up to point to the bucket
  return `https://${token.data.url.split('/').slice(-1)[0]}/${token.data.fields.key}`;
}

const toGetManyParams = ({
  pagination = {},
  sort = {},
  target,
  id,
  filter,
}) => ({
  filters: {
    ...filter,
    [target]: id,
  },
  offset: pagination.perPage && pagination.perPage * (pagination.page - 1),
  limit: pagination.perPage,
  sortField: sort.field,
  sortDirection: sort.order,
});


const getOneResource = async (resource, id) => {
  if (resource === 'privateQuotes') {
    const data = await get(replacePlaceholders(API_QUOTES_PRIVATE_QUOTES_URL_EDITFORMAT, { id }));
    return data.privateQuote;
  }
  if (resource === 'pushNotifications') {
    const data = await get(replacePlaceholders(`${API_NOTIFICATIONS_URL}/{id}`, { id }));
    return data.notification;
  }
  // if (resource === 'offeringCampaigns') {
  //   return get(replacePlaceholders(API_OFFER_CAMPAIGN_URL, { campaignId: id }));
  // }
  if (!TOP_LEVEL_RESOURCES.includes(resource)) {
    return null;
  }
  const data = await get(replacePlaceholders(
    API_TOP_LEVEL_RESOURCE_ONE_URL,
    { resource: kebabCase(resource), id },
  ));
  const key = Object.keys(data).find(k => resource.startsWith(k));
  const obj = data[key];

  // If the above `obj` has any fields that end in `Url` those fields are
  // copied to similar fields prefixed with _. For example if `obj.logoUrl`
  // exists then `obj._logoUrl` will added below.
  //
  // This is because FileInput (and related ImageInput) fields render previews
  // of existing files in its children FileField (or ImageField). And the field
  // needs to be an object.
  //
  // For example assume the obj has a logoUrl like like:
  //
  // obj = {
  //   logoUrl: 'https://...',
  //   ...other fields
  // }
  //
  // In this objects edit form we want to show the existing image preview
  // and use an ImageInput to allow the user to edit the image so we include
  // a ImageInput like:
  //
  //   <ImageInput source="_logoUrl" label="Logo" accept="image/*">
  //     <ImageField source="src" title="title" />
  //   </ImageInput>
  //
  // Notice that `_logoUrl` is used as the input soruce instead of `logoUrl`.
  // This is because the nested ImageField needs an object with a `src` property
  // to render the preview.
  //
  // The nested ImageField expects an object because if the user uses the ImageInput
  // to replace the logo the ImageField is passed a RawFile object which has
  // a `src` property refers to a local file and the image can be previewed
  // before uploading.
  //
  // This loop adds _*Url fields to the object:
  Object.keys(obj).filter(k => k.endsWith('Url')).forEach((k) => {
    obj[`_${k}`] = {
      src: obj[k],
    };
  });

  if (resource === 'offerings') {
    // Map each date string into an object so we can use it within an ArrayInput
    obj.closeDates = obj.closeDates && obj.closeDates.map(date => ({ date }));
  }
  return obj;
};


export const FOLIO_RESOURCES = [
  'folioPrivatePlacementOffers',
];

export const MAILCHIMP_RESOURCES = [
  'mailchimpAudiences',
];


async function getAdaminStats(initialUrl) {
  const { url } = await callWebApi('POST', initialUrl, null, getAuthToken());

  // If is S3 url with a Signature then don't include Authorization header
  // the GET request because it'll fail.
  const isSignedS3Url = url.match(/^https:\/\/s3\.([\w-]+\.)?amazonaws\.com\/.*Signature=.*$/);
  return { data: await get(url, null, null, { includeAuth: !isSignedS3Url }) };
}


export default async (type, resource, params) => {
  debug('%s: %s %o', type, resource, params);
  // eslint-disable-next-line no-console
  console.log(type, resource, params);

  if (FOLIO_RESOURCES.includes(resource)) {
    const folioResourceName = resource.slice(5).toLowerCase();

    // react-admin expect and `id` property in each object. Folio's
    // id are named after the singular name of resource
    const folioIdKey = `${camelCase(resource.slice(5, -1))}Oid`;

    switch (type) {
      case GET_MANY:
      case GET_MANY_REFERENCE:
      case GET_LIST: {
        if (resource === 'userRepresentatives') {
          const { data, count } = await get(API_USERS_URL, toGetManyParams(params));
          return {
            data: data.map(obj => ({
              id: obj[folioIdKey],
              ...obj,
            })),
            total: count || data.length,
          };
        }
        const { data, count } = await get(`${API_FOLIO_URLS}/${folioResourceName}`);
        return {
          data: data.map(obj => ({
            id: obj[folioIdKey],
            ...obj,
          })),
          total: count || data.length,
        };
      }
      case GET_ONE: {
        const { data } = await get(`${API_FOLIO_URLS}/${folioResourceName}/${params.id}`);
        return {
          data: {
            id: data[folioIdKey],
            ...data,
          },
        };
      }
      default: {
        debug('Unsupported type: %s', type);
        throw new Error(`Unsupported type: ${type}`);
      }
    }
  }

  if (MAILCHIMP_RESOURCES.includes(resource)) {
    const mailchimpResourceName = resource.slice(9).toLowerCase();

    switch (type) {
      case GET_MANY:
      case GET_MANY_REFERENCE:
      case GET_LIST: {
        const res = await get(`${API_MAILCHIMP_URLS}/${mailchimpResourceName}`);
        return {
          data: res.data.map(obj => ({
            id: obj.id,
            name: obj.name,
            visibility: obj.visibility,
            memberCount: obj.stats.member_count,
            segments: obj.segments,
          })),
          total: res.data.length,
        };
      }
      case GET_ONE: {
        const res = await get(`${API_MAILCHIMP_URLS}/${mailchimpResourceName}/${params.id}`);
        return {
          data: {
            id: res.data.id,
            ...res.data,
          },
        };
      }
      default: {
        debug('Unsupported type: %s', type);
        throw new Error(`Unsupported type: ${type}`);
      }
    }
  }

  switch (type) {
    case GET_MANY_REFERENCE:
    case GET_LIST: {
      if (TOP_LEVEL_RESOURCES.includes(resource)) {
        const { action } = params;
        if (resource === 'emailTemplates' && action === 'get-context-models') {
          const { contextModels } = await get(`${API_EMAIL_TEMPLATES}/context-models`);
          return {
            data: contextModels,
          };
        } if (resource === 'emailTemplates' && action === 'get-static-content') {
          const { statics } = await get(`${API_EMAIL_TEMPLATES}/statics`);
          return {
            data: statics,
          };
        } else if (resource === 'userActions') {
          const url = `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ONE_URL, { resource: 'users', id: params.id })}/actions`;
          const data = await get(url, toGetManyParams(params));
          return {
            data: data[resource],
            total: data[resource].length,
          };
        } else if (resource === 'configs') {
          const data = await get(API_CONFIGS_URL, toGetManyParams(params));
          return {
            data: data.configs,
            total: data.count,
          };
        }
        const data = await get(
          replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: kebabCase(resource) }),
          toGetManyParams(params),
        );
        return {
          data: data[resource],
          total: data.count || data[resource].length,
        };
      } else if (resource === 'pushNotifications') {
        const url = API_NOTIFICATIONS_URL;
        const res = await get(url, toGetManyParams(params));
        const data = res.notifications;
        return {
          data,
          total: res.count,
        };
      } else if (resource === 'pushNotificationRegistrations') {
        const url = API_NOTIFICATION_REGISTRATIONS_URL;
        const res = await get(url, toGetManyParams(params));
        const data = res.registrations;
        return {
          data,
          total: res.count,
        };
      } else if (ACCOUNT_RESOURCES.includes(resource)) {
        if (resource === 'accountPositions') {
          const url = `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: 'accounts' })}/${params.id}/positions`;
          const data = await get(url, toGetManyParams(params));
          return {
            data: data[resource].map(o => ({ id: o.security.ticker, ...o })),
            total: data[resource].length,
          };
        } else if (resource === 'accountTransactions') {
          const url = `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: 'accounts' })}/${params.id}/transactions`;
          const data = await get(url, toGetManyParams(params));
          // Transaction from backend don't include a unique id
          return {
            data: data[resource].map(o => ({ id: uniqueId(), ...o })),
            total: data[resource].length,
          };
        }
        const subresource = 'restrictions';
        const url = `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: 'accounts' })}/${params.id}/${subresource}`;
        const data = await get(url, toGetManyParams(params));
        return {
          data: data[resource].map(o => ({ id: o[`${resource.slice(0, -1)}Id`], ...o })),
          total: data.count || data[resource].length,
        };
      } else if (resource === 'offeringCampaigns') {
        const data = await get(API_OFFER_CAMPAIGNS_URL, toGetManyParams(params));
        return {
          data: data[resource],
          total: data.count || data[resource].length,
        };
      } else if (resource === 'privateQuotes') {
        const data = await get(API_QUOTES_PRIVATE_QUOTES_ALL_URL, toGetManyParams(params));
        return {
          data: data[resource],
          total: data.count || data[resource].length,
        };
      } else if (resource === 'newAccounts') {
        const url = replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: 'users' });
        const tempParams = {
          ...params,
          filter: {
            ...params.filter,
            approvedAt: '_null_',
            signUpCompletedAt: '_not_null_',
          },
        };

        const data = await get(url, toGetManyParams(tempParams));

        const completeData = await Promise.all(data.users.map(async (user) => {
          const mUrl = replacePlaceholders(API_USER_URL, { userId: user.id });
          const mData = await get(mUrl);

          return { ...mData.user };
        }));

        return {
          data: completeData.map(o => ({ ...o })),
          total: data.count || data.users.length,
        };
      } else if (resource === 'externalOfferOrders') {
        const data = await get(API_EXTERNAL_OFFER_ORDERS_URL, toGetManyParams(params));
        return {
          data: data[resource],
          total: data.count,
        };
      } else if (resource === 'userRepresentatives') {
        const data = await get(API_USERS_URL, toGetManyParams(params));
        return {
          data: data.users,
          total: data.count,
        };
      }
      return { data: [], total: 0 };
    }
    case GET_ONE: {
      const { action } = params;
      if (resource === 'emailTemplates' && params.action === 'get-one') {
        const data = await get(replacePlaceholders(API_EMAIL_TEMPLATE, { id: params.id }));
        return {
          data: {
            ...data.emailTemplate,
          },
        };
      } else if (resource === 'emailTemplates' && params.action === 'model' && params.model === 'Offer' && params.id) {
        const data = await get(`${API_EMAIL_TEMPLATES}/offer/${params.id}`);
        return {
          data: {
            ...data.offer,
            id: params.id,
          },
        };
      } else if (resource === 'offerings' && action === 'get-reduction-results') {
        const { record, reductionType, percentage } = params;
        const {
          id,
          totalRaisedAmount,
        } = record;
        const result = await get(`${API_OFFERING_ACTIONS_URL}/oversubscription/${id}/getReductionResults?reductionType=${reductionType}&percentage=${percentage}&totalRaisedAmount=${totalRaisedAmount}`);
        return { data: { id, result } };
      } else if (resource === 'offerings') {
        const data = await getOneResource(resource, params.id);
        if (data.investmentRange) {
          data.investmentRanges = [];
          data.investmentRange.split(';').forEach((range) => {
            data.investmentRanges.push({ investmentRange: range });
          });
        }
        const updatedResult = { ...data };
        if (!_.isEmpty(updatedResult.offerOrderSummation) && updatedResult.offerOrderSummation.indexOf(',') >= 0) {
          const splat = updatedResult.offerOrderSummation.split(',');
          updatedResult.offerOrderSummation = {
            created: splat.includes('created'),
            submitted: splat.includes('submitted'),
            deleted: splat.includes('deleted'),
            completed: splat.includes('completed'),
            rejected: splat.includes('rejected'),
            cancelled: splat.includes('cancelled'),
            useCustom: false,
            custom: null,
          };
        } else if (!_.isEmpty(updatedResult.offerOrderSummation)) {
          updatedResult.offerOrderSummation = {
            created: false,
            submitted: false,
            deleted: false,
            completed: false,
            rejected: false,
            cancelled: false,
            useCustom: true,
            custom: updatedResult.offerOrderSummation,
          };
        } else {
          updatedResult.offerOrderSummation = {
            created: false,
            submitted: false,
            deleted: false,
            completed: false,
            rejected: false,
            cancelled: false,
            useCustom: false,
            custom: null,
          };
        }
        return { data: updatedResult };
      }
      return { data: await getOneResource(resource, params.id) };
    }
    case GET_MANY: {
      if (ACCOUNT_RESOURCES.includes(resource)) {
        const path = kebabCase(resource.replace('account', ''));
        const url = `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: 'accounts' })}/${params.ids[0]}/${path}`;
        const data = await get(url);
        return {
          data: [{ id: params.ids[0], ...data[resource] }],
        };
      }

      if (resource === 'userFiles') {
        // We're only expecting these to be drivers licenses for the time being
        const url = replacePlaceholders(API_USER_FILES_ONE_URL, { userId: params.ids[0], name: 'driver-license' });

        return { data: [{ id: params.ids[0], url: `${url}?authToken=${getAuthToken()}` }] };
      }

      if (resource === 'users' && params && params.ids && params.ids.length === 1) {
        const url = replacePlaceholders(API_USER_URL, { userId: params.ids[0] });
        const data = await get(url);
        return {
          data: [{ ...data.user }],
        };
      }

      if (resource === 'privateQuotes') {
        await callWebApi('GET', API_QUOTES_PRIVATE_QUOTES_FOLIO_URL, null, getAuthToken());
        return { data: [] };
      }

      if (resource === 'userRepresentatives') {
        const url = replacePlaceholders(API_USERS_URL);
        const data = await get(url);
        return {
          data: data.users,
        };
      }

      const res = await get(
        replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: kebabCase(resource) }),
        toGetManyParams({
          filter: {
            _IN_id: params.ids.join(','),
          },
        }),
      );
      return { data: res[resource] };
    }
    case CREATE: {
      if (resource === 'emailTemplates') {
        const { data } = params;
        const result = await callWebApi('POST', API_EMAIL_TEMPLATES, data, getAuthToken());
        return result;
      }

      if (resource === 'users') {
        const { data } = params;
        const { role, ...userInfo } = data;

        const { token } = await callWebApi('POST', API_AUTH_CREDENTIALS_URL, userInfo, getAuthToken());
        const userId = jwt.decode(token).user.id;
        const userUrl = replacePlaceholders(
          API_USER_URL,
          { userId },
        );

        await callWebApi('PATCH', userUrl, { role }, getAuthToken());

        return { data: { id: userId } };
      }

      if (resource === 'pushNotifications') {
        const { data } = params;
        const { notificationId } = await callWebApi(
          'POST',
          API_NOTIFICATIONS_URL,
          data,
          getAuthToken(),
        );
        return {
          data: {
            id: notificationId,
          },
        };
      } else if (resource === 'offeringOrders') {
        const { data } = params;
        const { offerOrderId } = await callWebApi(
          'POST',
          `${replacePlaceholders(API_ACCOUNT_OFFER_ORDERS_URL, data)}`,
          data,
          getAuthToken(),
        );
        data.id = offerOrderId;
        return { data };
      } else if (resource === 'offeringCampaigns') {
        const { data } = params;
        const { offerCampaign } = await callWebApi(
          'POST',
          API_OFFER_CAMPAIGNS_URL,
          data,
          getAuthToken(),
        );
        data.id = offerCampaign.id;
        return { data };
      } else if (resource === 'offeringDocuments') {
        const {
          data: {
            _document,
            ...rest
          },
        } = params;

        rest.url = await uploadFile(`offers/${rest.offerId}/offer-document-${_document.title}`, _document.rawFile);

        const url = replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ALL_URL,
          { resource: kebabCase(resource) },
        );

        const { offeringDocuments } = await callWebApi('POST', url, { docs: [rest] }, getAuthToken());

        return { data: offeringDocuments[0] };
      } else if (resource === 'offerings') {
        const { data } = params;
        const { documents, assets, _logoUrl } = data;

        delete data.assets;
        delete data.documents;

        if (data.closeDates) {
          // Change our dates back into strings
          data.closeDates = data.closeDates.map(date => date.date);
        }

        if (data.investmentRanges) {
          const investmentRanges = [];
          data.investmentRanges.forEach((range) => {
            investmentRanges.push(range.investmentRange);
          });
          data.investmentRange = investmentRanges.join(';');
        }
        if (data.isExternalOffer) {
          data.folioId = null;
        }
        // Create our offer first so we can get its id
        const { offer } = await callWebApi(
          'POST',
          API_OFFERINGS_URL,
          data,
          getAuthToken(),
        );

        if (_logoUrl && _logoUrl.rawFile) {
          const logoUrl = await uploadFile(`offers/${offer.id}/logo-${_logoUrl.title}`, _logoUrl.rawFile);
          const updateParams = { logoUrl };
          const url = replacePlaceholders(
            API_TOP_LEVEL_RESOURCE_ONE_URL,
            { resource: 'offerings', id: offer.id },
          );
          await callWebApi('PATCH', url, updateParams, getAuthToken());
          offer.logoUrl = logoUrl;
        }

        if (assets) {
          // Map through our new assets and upload them
          const newAssets = await Promise.all(assets.map(async (asset) => {
            const {
              _asset,
              isVideo,
              vimeoUrl,
            } = asset;

            const url = await uploadFile(`offers/${offer.id}/asset-${_asset.title}`, _asset.rawFile);
            if (isVideo) {
              return {
                type: 'vimeo',
                previewImageUrl: url,
                url: vimeoUrl,
              };
            }

            return {
              url,
              type: _asset.rawFile.type,
            };
          }));

          // Add our assets into the newly created offer
          await callWebApi(
            'PATCH',
            `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ONE_URL, { resource: 'offerings', id: offer.id })}`,
            { assets: newAssets },
            getAuthToken(),
          );

          // Add our new assets into the newly created offer
          offer.assets = newAssets;
        }

        if (documents) {
          // Map through our documents, upload them, and then add the new offerId  and url to them
          const mappedDocs = await Promise.all(documents.map(async (doc) => {
            const {
              _document,
              ...rest
            } = doc;

            const url = await uploadFile(`offers/${offer.id}/offer-document-${_document.title}`, _document.rawFile);

            return {
              ...rest,
              url,
              offerId: offer.id,
            };
          }));

          const { offeringDocuments } = await callWebApi(
            'POST',
            `${replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: 'offering-documents' })}`,
            { docs: mappedDocs },
            getAuthToken(),
          );

          // Put our newly created documents into the offer
          offer.documents = offeringDocuments;
        }

        return { data: offer };
      } else if (resource === 'privateQuotes') {
        const res = await callWebApi(
          'POST',
          API_QUOTES_PRIVATE_QUOTES_ALL_URL,
          params.data,
          getAuthToken(),
        );
        return { data: res.privateQuote };
      } else if (resource === 'configs') {
        const res = await callWebApi(
          'POST',
          API_CONFIGS_URL,
          params.data,
          getAuthToken(),
        );
        return { data: res.config };
      }
      throw new Error(`Unsupported action: ${type} for resource: ${resource}`);
    }
    case UPDATE: {
      const { id, action, data } = params;
      if (resource === 'emailTemplates' && action === 'update') {
        const url = replacePlaceholders(
          API_EMAIL_TEMPLATE,
          { id: params.id },
        );

        const result = await callWebApi('PUT', url, data, getAuthToken());
        return result;
      }
      if (resource === 'offeringCampaigns' && action === 'send-to-mailchimp') {
        const url = replacePlaceholders(
          API_OFFER_CAMPAIGN_SEND_URL,
          { campaignId: id },
        );

        await callWebApi('POST', url, null, getAuthToken());
        return { data: { id } };
      }
      if (resource === 'offeringCampaigns' && action === 'send-test-to-mailchimp') {
        const url = replacePlaceholders(
          API_OFFER_CAMPAIGN_TEST_SEND_URL,
          { campaignId: id },
        );

        await callWebApi('POST', url, { testEmails: {} }, getAuthToken());
        return { data: { id } };
      }
      if (resource === 'offeringCampaigns' && action === 'evaluate-audience-segment-condition') {
        const {
          emailTemplateId,
          audience,
        } = data;
        const url = replacePlaceholders(
          API_OFFER_CAMPAIGN_EVALUATE_URL,
          { campaignId: id },
        );

        const { segmentValidationMsg } = await callWebApi('PUT', url, {
          emailTemplateId,
          audience,
        }, getAuthToken());
        return { data: { id, segmentValidationMsg } };
      }
      if (resource === 'offeringCampaigns') {
        const {
          listId,
          offerId,
          segmentType,
          emailTemplateId,
        } = data;
        const url = replacePlaceholders(
          API_OFFER_CAMPAIGN_URL,
          { campaignId: id },
        );
        await callWebApi(
          'PUT',
          url,
          {
            audienceId: listId,
            offerId,
            segmentType,
            emailTemplateId,
          },
          getAuthToken(),
        );
        return { data: { id } };
      }
      if (resource === 'offeringOrders' && action === 'submit-to-folio') {
        const url = `${replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        )}/folio`;

        await callWebApi('POST', url, null, getAuthToken());
        return { data: { id } };
      }
      if (resource === 'offerings' && action === 'apply-reduction-results') {
        const {
          record,
          reductionType,
          percentage,
          updatedOrders,
        } = params;
        const url = `${API_OFFERING_ACTIONS_URL}/oversubscription/${record.id}/applyOfferReduction`;
        const payload = {
          updatedOrders,
          reductionType,
          percentage,
        };
        const result = await callWebApi('PUT', url, payload, getAuthToken());
        return result;
      }
      if (resource === 'offerings') {
        const {
          _logoUrl,
          _assets,
          ...rest
        } = data;

        if (_assets) {
          // Map through our new assets and upload them
          const newAssets = await Promise.all(_assets.map(async (asset) => {
            const {
              _asset,
              isVideo,
              vimeoUrl,
            } = asset;

            const url = await uploadFile(`offers/${id}/asset-${_asset.title.replace(/ /g, '-')}`, _asset.rawFile);
            if (isVideo) {
              return {
                type: 'vimeo',
                previewImageUrl: url,
                url: vimeoUrl,
              };
            }

            return {
              url,
              type: _asset.rawFile.type,
            };
          }));

          // We might not have any assets on this offer yet when updating
          if (rest.assets) {
            rest.assets.push(...newAssets);
          } else {
            rest.assets = newAssets;
          }
        }

        if (_logoUrl && _logoUrl.rawFile) {
          rest.logoUrl = await uploadFile(`offers/${id}/logo-${_logoUrl.title}`, _logoUrl.rawFile);
        }
        const url = replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        );

        // rest.closeDates is an list of objects with a `date` prop:
        // [
        //    { date: <Date>, },
        //    { date: <Date>, },
        // ]
        // The backend expects a list of date strings:
        // [
        //   '2018-01-01',
        //   '2018-01-02',
        // ]
        const flattenCloseDate = (obj) => {
          if (obj.date instanceof Date) {
            return obj.date.toISOString();
          } else if (typeof obj.date === 'object' && obj.date.date) {
            // Old version of code mangled data into nested objects
            return flattenCloseDate(obj.date);
          } else if (obj.date) {
            return obj.date;
          }
          return obj;
        };

        // closeDates is null/undef until 'recurring' is toggled at some point
        //   causing the map to error
        if (rest.closeDates) {
          rest.closeDates = rest.closeDates.map(flattenCloseDate);
        }

        if (rest.investmentRanges) {
          const investmentRanges = [];
          rest.investmentRanges.forEach((range) => {
            investmentRanges.push(range.investmentRange);
          });
          rest.investmentRange = investmentRanges.join(';');
        }

        // These get set to '' for some reason when no number is present. Set these
        //   to null so updates actually stick as intended
        if (rest.curTotalRaiseCurrencyAmt === '') {
          rest.curTotalRaiseCurrencyAmt = null;
        }
        if (rest.maximumInvestCurrencyAmt === '') {
          rest.maximumInvestCurrencyAmt = null;
        }
        if (rest.folioId === '' || rest.isExternalOffer) {
          rest.folioId = null;
        }


        const summation = [];
        if (!rest.offerOrderSummation.useCustom) {
          if (rest.offerOrderSummation.created) {
            summation.push('created');
          }
          if (rest.offerOrderSummation.submitted) {
            summation.push('submitted');
          }
          if (rest.offerOrderSummation.deleted) {
            summation.push('deleted');
          }
          if (rest.offerOrderSummation.completed) {
            summation.push('completed');
          }
          if (rest.offerOrderSummation.rejected) {
            summation.push('rejected');
          }
          if (rest.offerOrderSummation.cancelled) {
            summation.push('cancelled');
          }
          rest.offerOrderSummation = summation.length === 0 ? '' : `${summation.join(',')},`;
        } else {
          rest.offerOrderSummation = rest.offerOrderSummation.custom.toString();
        }

        await callWebApi('PATCH', url, rest, getAuthToken());
        return { data: { id, ...rest } };
      }
      if (resource === 'users') {
        const url = replacePlaceholders(
          API_USER_URL,
          { userId: id },
        );

        // Check if the email was changed. Delete if not, since the backend will check this
        //   and fail since that email is already in use.
        if (params.previousData && data.email === params.previousData.email) {
          delete data.email;
        }

        await callWebApi('PATCH', url, data, getAuthToken());

        return { data: { id, ...data } };
      }
      if (resource === 'privateQuotes') {
        const url = replacePlaceholders(API_QUOTES_PRIVATE_QUOTES_URL, { id });
        await callWebApi('PATCH', url, data, getAuthToken());
        return { data: { id, ...data } };
      }
      if (resource === 'offeringDocuments') {
        const { _offerDocument, ...rest } = data;

        if (_offerDocument && _offerDocument.rawFile) {
          rest.url = await uploadFile(`offers/${id}/offer-document-${_offerDocument.title}`, _offerDocument.rawFile);
        }

        const url = replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        );

        await callWebApi('PATCH', url, rest, getAuthToken());
        return { data: { id, ...rest } };
      }
      if (resource === 'solicitedUsers') {
        const url = replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        );
        await callWebApi('PATCH', url, { userId: id, solicitedUser: data }, getAuthToken());
        return { data: { id, ...data } };
      }
      if (resource === 'offeringOrders') {
        const url = replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        );
        await callWebApi('PATCH', url, { solicitedBy: data.solicitedBy }, getAuthToken());
        return { data: { id, ...data } };
      }
      if (resource === 'externalOfferOrders') {
        const url = replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        );
        await callWebApi('PATCH', url, { ...data }, getAuthToken());
        return { data: { id, ...data } };
      }
      if (resource === 'configs') {
        const url = `${replacePlaceholders(
          API_TOP_LEVEL_RESOURCE_ONE_URL,
          { resource: kebabCase(resource), id },
        )}`;
        await callWebApi('PATCH', url, { ...data }, getAuthToken());
        return { data: { id, ...data } };
      }
      throw new Error(`Unsupported action: ${type} for resource: ${resource}`);
    }
    case DELETE: {
      const { action, data } = params;
      if (resource === 'offeringOrders' && action === 'cancel-offer-order') {
        const { accountNumber, id } = data;
        const url = replacePlaceholders(
          `${API_OFFERING_ORDER_URL}/{orderId}`,
          { accountNumber, orderId: id },
        );

        await callWebApi(
          'DELETE',
          url,
          null,
          getAuthToken(),
        );

        return { data: { id } };
      }
      if (resource === 'offeringDocuments') {
        const { id } = params;
        const url = replacePlaceholders(`${API_OFFERING_DOCUMENTS_URL}/{id}`, { id });
        await callWebApi('DELETE', url, null, getAuthToken());
        return { data: { id } };
      }
      if (resource === 'privateQuotes') {
        const { id } = params;
        const url = replacePlaceholders(`${API_QUOTES_PRIVATE_QUOTES_URL}`, { id });
        await callWebApi('DELETE', url, null, getAuthToken());
        return { data: { id } };
      }
      return { data: {} };
    }
    case DELETE_MANY: {
      const { ids } = params;
      const data = await Promise.all(ids.map(async (id) => {
        const url = replacePlaceholders(API_OFFER_ORDER_URL, { offerOrderId: id });
        const mData = await callWebApi(
          'DELETE',
          url,
          null,
          getAuthToken(),
        );

        return { ...mData };
      }));

      return { data: [...data] };
    }

    case UPDATE_MANY: {
      if (resource === 'newAccounts') {
        const { ids } = params;
        await Promise.all(ids.map(async (id) => {
          const url = replacePlaceholders(API_TOP_LEVEL_RESOURCE_APPROVE_URL, { resource: 'users', id });
          await callWebApi(
            'POST',
            url,
            null,
            getAuthToken(),
          );
        }));

        return { data: ids };
      }
      const data = await callWebApi('POST', API_UPLOAD_OFFER_ORDERS_FOLIO_URL, { ids: params.ids }, getAuthToken());
      return { data: data.offeringOrders };
    }

    // Custom dataProvider action type to 'approve' an object
    case 'APPROVE_ONE': {
      const { id } = params;
      const url = replacePlaceholders(API_TOP_LEVEL_RESOURCE_APPROVE_URL, { resource, id });
      await callWebApi('POST', url, null, getAuthToken());
      return { data: { id } };
    }

    case 'CHANGESTATUS_ONE': {
      const { id, status } = params;
      const url = replacePlaceholders(API_USER_CHANGE_STATUS_URL, { resource, userId: id });
      await callWebApi('POST', url, { status }, getAuthToken());
      return { data: { id } };
    }

    // Custom dataProvider action type to create a pw reset
    case 'RESET_PASSWORD': {
      const { id, email, isAdmin } = params;
      await callWebApi('POST', API_SEND_RESET_PASSWORD_URL, { email, isAdmin }, getAuthToken());
      return { data: { id } };
    }

    case 'DELETE_TRUSTED_CONTACT': {
      const { userId, accountNumber } = params;
      const url = replacePlaceholders(API_USER_TRUSTED_CONTACT_URL, { userId, accountNumber });
      await callWebApi('DELETE', `${url}/tcp`, null, getAuthToken());
      return { data: { userId } };
    }

    // Custom dataProvider action types to get dashboard data
    case 'DASHBOARD_STATISTICS': {
      return getAdaminStats(API_ADMIN_STATISICS_URL);
    }
    case 'DASHBOARD_CHART_DATA': {
      return getAdaminStats(API_ADMIN_CHART_DATA_URL);
    }

    case 'SINGLE_FOLIO_SECURITY': {
      const { ticker } = params;
      const res = await callWebApi('GET', `${API_QUOTES_PRIVATE_QUOTES_FOLIO_URL}/${ticker}`, null, getAuthToken());
      return { data: { id: res.id } };
    }

    case 'CUSTOM_EXPORT': {
      const data = await get(
        replacePlaceholders(API_TOP_LEVEL_RESOURCE_ALL_URL, { resource: kebabCase(resource) }),
        toGetManyParams(params),
      );
      return {
        data: data[resource],
        total: data.count || data[resource].length,
      };
    }

    case 'EXTERNAL_OFFER_ORDER_SEND_STATUS_EMAIL': {
      const { id } = params;
      const url = replacePlaceholders(
        EXTERNAL_OFFER_ORDER_STATUS_EMAIL,
        { resource: kebabCase(resource), id },
      );
      await callWebApi('PATCH', url, null, getAuthToken());
      return { data: {} };
    }

    case 'CLOSE_ACCOUNT_ONE': {
      const { userId } = params;
      const url = replacePlaceholders(API_ACCOUNT_CLOSE_ACCOUNT_URL, { resource, userId });
      await callWebApi('PUT', url, null, getAuthToken());
      return { data: { id: userId } };
    }

    default: {
      debug('Unsupported type: %s', type);
      throw new Error(`Unsupported type: ${type}`);
    }
  }
};



// WEBPACK FOOTER //
// ./src/dataProvider.js